import { useState, useEffect } from 'react';
import api from '@/services/api';
import socketService from '@/services/socket';

interface RetentionPolicy {
  globalEnabled: boolean;
  channelRetentionDays: number | null;
  dmRetentionDays: number | null;
  fileRetentionDays: number | null;
  applyToPrivateChannels: boolean;
  applyToPublicChannels: boolean;
  applyToDirectMessages: boolean;
  lastCleanupAt: string | null;
}

export default function RetentionPolicySettings() {
  const [policy, setPolicy] = useState<RetentionPolicy>({
    globalEnabled: false,
    channelRetentionDays: null,
    dmRetentionDays: null,
    fileRetentionDays: null,
    applyToPrivateChannels: true,
    applyToPublicChannels: true,
    applyToDirectMessages: true,
    lastCleanupAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchPolicy();
  }, []);

  useEffect(() => {
    const handleRetentionUpdated = (data: any) => {
      if (data?.policy) {
        setPolicy(data.policy);
        setMessage('Retention policy updated (websocket)');
      }
    };

    socketService.on('admin:retention-updated', handleRetentionUpdated);
    return () => socketService.off('admin:retention-updated', handleRetentionUpdated);
  }, []);

  const fetchPolicy = async () => {
    try {
      const response = await api.get('/retention');
      setPolicy(response.data.policy);
    } catch (error) {
      console.error('Error fetching retention policy:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await api.put('/retention', policy);
      if (response.data.policy) {
        setPolicy(response.data.policy);
      }
      setMessage('Retention policy saved successfully');
    } catch (error: any) {
      // Don't show duplicate toast for 403 errors - global handler already shows it
      if (error.response?.status !== 403) {
        setMessage(error.response?.data?.error || 'Failed to save retention policy');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRunCleanup = async () => {
    if (
      !confirm(
        'Are you sure you want to run cleanup now? This will permanently delete old messages based on your retention policy.',
      )
    ) {
      return;
    }

    setRunning(true);
    setMessage('');
    try {
      const response = await api.post('/retention/cleanup');
      if (response.data.policy) {
        setPolicy(response.data.policy);
      }
      const stats = response.data.stats || { deletedMessages: 0, deletedDMs: 0, deletedFiles: 0 };
      setMessage(
        `Cleanup completed: ${stats.deletedMessages} messages, ${stats.deletedDMs} DMs, ${stats.deletedFiles} files deleted`,
      );
    } catch (error: any) {
      // Don't show duplicate toast for 403 errors - global handler already shows it
      if (error.response?.status !== 403) {
        setMessage(error.response?.data?.error || 'Failed to run cleanup');
      }
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 bg-brand-surface dark:bg-brand-surface rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-brand-on-background dark:text-brand-on-primary">
        Retention Policy (Owner Only)
      </h2>

      {message && (
        <div
          className={`mb-4 p-3 rounded ${message.includes('successfully') || message.includes('completed') ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'}`}
        >
          {message}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={policy.globalEnabled}
            onChange={(e) => setPolicy({ ...policy, globalEnabled: e.target.checked })}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <label className="ml-2 text-sm font-medium text-brand-on-background dark:text-brand-on-primary">
            Enable global retention policy
          </label>
        </div>

        {policy.globalEnabled && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
                  Channel message retention (days)
                </label>
                <input
                  type="number"
                  min="1"
                  value={policy.channelRetentionDays || ''}
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      channelRetentionDays: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="No limit"
                  className="input"
                />
                <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant mt-1">
                  Leave empty for no limit
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
                  DM retention (days)
                </label>
                <input
                  type="number"
                  min="1"
                  value={policy.dmRetentionDays || ''}
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      dmRetentionDays: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="No limit"
                  className="input"
                />
                <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant mt-1">
                  Leave empty for no limit
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
                  File attachment retention (days)
                </label>
                <input
                  type="number"
                  min="1"
                  value={policy.fileRetentionDays || ''}
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      fileRetentionDays: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="No limit"
                  className="input"
                />
                <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant mt-1">
                  Leave empty for no limit
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant">
                Apply retention to:
              </p>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={policy.applyToPublicChannels}
                  onChange={(e) =>
                    setPolicy({ ...policy, applyToPublicChannels: e.target.checked })
                  }
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-brand-on-background dark:text-brand-on-primary">
                  Public channels
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={policy.applyToPrivateChannels}
                  onChange={(e) =>
                    setPolicy({ ...policy, applyToPrivateChannels: e.target.checked })
                  }
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-brand-on-background dark:text-brand-on-primary">
                  Private channels
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={policy.applyToDirectMessages}
                  onChange={(e) =>
                    setPolicy({ ...policy, applyToDirectMessages: e.target.checked })
                  }
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-brand-on-background dark:text-brand-on-primary">
                  Direct messages
                </label>
              </div>
            </div>

            {policy.lastCleanupAt && (
              <div className="text-sm text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                Last cleanup: {new Date(policy.lastCleanupAt).toLocaleString()}
              </div>
            )}
          </>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button onClick={handleSave} disabled={saving} className="btn btn-primary">
            {saving ? 'Saving...' : 'Save Policy'}
          </button>

          {policy.globalEnabled && (
            <button
              onClick={handleRunCleanup}
              disabled={running}
              className="btn bg-orange-600 hover:bg-orange-700 text-brand-on-primary"
            >
              {running ? 'Running...' : 'Run Cleanup Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
